/**
 * Lazy-loaded image with loading skeleton and error handling.
 */

import { ImageOff } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface MarkdownImageProps {
	src?: string;
	alt?: string;
	className?: string;
}

export function MarkdownImage({ src, alt, className }: MarkdownImageProps) {
	const [status, setStatus] = useState<"loading" | "loaded" | "error">("loading");

	if (!src) return null;

	return (
		<div className={cn("relative my-4", className)}>
			{status === "loading" && (
				<div className="absolute inset-0 bg-muted animate-pulse rounded-lg" />
			)}

			{status === "error" ? (
				<div className="flex items-center justify-center gap-2 p-8 bg-muted/50 rounded-lg border border-border/30 text-muted-foreground">
					<ImageOff className="h-5 w-5" />
					<span className="text-sm">Failed to load image</span>
				</div>
			) : (
				<img
					src={src}
					alt={alt || ""}
					loading="lazy"
					className={cn(
						"rounded-lg max-w-full h-auto transition-opacity duration-300",
						status === "loading" ? "opacity-0" : "opacity-100"
					)}
					onLoad={() => setStatus("loaded")}
					onError={() => setStatus("error")}
				/>
			)}
		</div>
	);
}
